export enum ChannelReviewSources {
  Airbnb = 'airbnb',
  BookingCom = 'booking.com',
  Expedia = 'expedia',
  Vrbo = 'vrbo',
}

export const channelReviewSourceOptions = [
  { value: ChannelReviewSources.Airbnb, name: 'Airbnb', color: '#FF9797', reviewable: true, automation: true },
  {
    value: ChannelReviewSources.BookingCom,
    name: 'Booking.com',
    color: '#006ACB',
    reviewable: true,
    automation: false,
  },
  { value: ChannelReviewSources.Expedia, name: 'Expedia', color: '#79BDFC', reviewable: false, automation: false },
  { value: ChannelReviewSources.Vrbo, name: 'VRBO', color: '#0e214b', reviewable: true, automation: false },
]

export function getChannelReviewSource(source: string) {
  if (/^airbnb/i.test(source)) return ChannelReviewSources.Airbnb
  if (/^booking/i.test(source)) return ChannelReviewSources.BookingCom
  if (/^expedia/i.test(source)) return ChannelReviewSources.Expedia
  if (/^vrbo/i.test(source)) return ChannelReviewSources.Vrbo
  return null
}

export function isChannelReviewAvailable(source: string): boolean {
  return !!getChannelReviewSource(source)
}

export function isChannelReviewWritable(source: string) {
  const s = getChannelReviewSource(source)
  return s ? channelReviewSourceOptions.find((t) => t.value === s)?.reviewable : false
}

export function isChannelReviewAutomatable(source: string) {
  const s = getChannelReviewSource(source)
  return s ? channelReviewSourceOptions.find((t) => t.value === s)?.automation : false
}

export function getChannelMaxRating(source: ChannelReviewSources): number {
  switch (source) {
    case ChannelReviewSources.BookingCom:
      return 10
    default:
      return 5
  }
}

export interface ChannelReviewUser {
  id: string
  name: string
  role: string
}

export enum ReviewCategoryRatingType {
  FACILITIES = 'facilities',
  SERVICES = 'services',
  STAFF = 'staff',
  COMFORT = 'comfort',

  CLEANLINESS = 'cleanliness',
  COMMUNICATION = 'communication',
  RESPECT_HOUSE_RULES = 'respect_house_rules',
  ACCURACY = 'accuracy',
  CHECKIN = 'checkin',
  LOCATION = 'location',
  VALUE = 'value',

  ONLINE_LISTING = 'onlineListing',
  ROOM_CLEANLINESS = 'roomCleanliness',
  HOTEL_CONDITION = 'hotelCondition',
  ECO_FRIENDLINESS = 'ecoFriendliness',
  AREA_AROUND_HOTEL = 'areaAroundHotel',
  ROOM_AMENITIES_SCORE = 'roomAmenitiesScore',
  NEIGHBORHOOD_SATISFACTION = 'neighborhoodSatisfaction',
  CONVENIENCE_OF_LOCATION = 'convenienceOfLocation',
  ROOM_QUALITY = 'roomQuality',
}

export interface ChannelReviewCategoryRating {
  category: string // ReviewCategoryRatingType
  rating: number
  comment?: string
  review_category_tags?: string[]
}

export interface ReviewCategoryRatingFilter {
  category: string
  ratings: number[]
}

export interface ReviewRatingCategory {
  value: string
  name: string
}

export interface ChannelReviewItem {
  _id: string
  account: number
  rentalId: string
  inquiryId: string
  guestId: string
  propertyId: string
  listingId: string
  checkIn: string
  checkOut: string
  source: ChannelReviewSources
  reviewId: string
  reservationId: string
  reviewer: ChannelReviewUser
  reviewee: ChannelReviewUser
  title: string
  text: string
  positiveText: string
  negativeText: string
  published: boolean
  overallRating: number // 0
  channelOverallRating: number
  categoryRatings: ChannelReviewCategoryRating[]
  response?: {
    text: string
    respondedAt: string // '2024-08-01T04:25:46.934Z'
    lastChangeAt: string // '2024-08-01T04:25:46.934Z'
  }
  privateFeedback?: string
  isRevieweeRecommended: boolean
  isGenuine: boolean
  submitted: boolean
  submittedAt: string // '2024-08-01T04:25:46.934Z'
  hidden: boolean
  language: string
  reviewerCanSubmitUntil: string // '2024-08-01T04:25:46.934Z'
  reviewCreatedAt: string // '2024-08-01T04:25:46.934Z'
  createdAt: string // '2024-08-01T04:25:46.934Z'
  updatedAt: string // '2024-08-01T04:25:46.934Z'
  isEligibleForResponse: boolean
}

export function isGuestReviewRepliable(item: ChannelReviewItem): boolean {
  return item?.reviewer?.role === 'guest' && !item.response && item.isEligibleForResponse
}

export function isGuestReviewable(item: ChannelReviewItem): boolean {
  return item?.reviewer?.role === 'host' && !item.submitted && !isReviewGuestExpired(item)
}

export function isReviewGuestExpired(item: ChannelReviewItem): boolean {
  if (!item?.reviewerCanSubmitUntil) return true
  return new Date() >= new Date(item.reviewerCanSubmitUntil)
}

export interface ChannelReviewStatCategoryRatingItem {
  _id: string // category
  reviewsCount: number
  averageRating: number
  tags: Record<string, number>
}
export interface ChannelReviewRespondedCount {
  yes: number
  no: number
}
export interface ChannelRentalAVGRatingItem {
  averageOverallRating: number // 4.875
  source: string // 'airbnb'
  latestReview: {
    text: string // 'The host is very friendly and listen to you very carefully and try to facilitate you as well.\nBut the location is not so good.'
    _id: string // '66d21f6d43f7344f3de4d5f2'
    submittedAt: string // '2024-09-03T07:01:40.748Z'
  }
  rentalId: string // '4e9b0f23-7c26-4fcc-b2bf-f9425e6569d8'
}

export interface ChannelMonthlyRatingDistItem {
  ratings: {
    range: string // '4.8-5'
    count: number // 2
  }[]
  averageOverallRating: number
  month: string // '2024-1'
}

export interface ReviewsStatsCategoryRating {
  _id: string
  reviewsCount: number
  averageRating: number
}

export interface ReviewsStatsCategoryRatingByRental {
  rentalId: string
  categories: ReviewsStatsCategoryRating[]
}

export type FiveStarPercentageByRental = {
  rentalId: string
  source: ChannelReviewSources
  fiveStarPercentage: number
}

export type FiveStarPercentageByPeriod = {
  source: ChannelReviewSources
  granularity: 'day' | 'week' | 'month'
  // current period
  periodStart: string // YYYY-MM-DD
  fiveStarPercentage: number // 100, percent
  // previous period
  previousPeriodStart: string // YYYY-MM-DD
  previousFiveStarPercentage: number // 100, percent
}

export interface ChannelReviewsResponse {
  reviews: ChannelReviewItem[]
  counts: {
    total: number
    sources: Record<ChannelReviewSources, number>
    responded: ChannelReviewRespondedCount
    byMonth: (Record<ChannelReviewSources, { responded: ChannelReviewRespondedCount }> & { month: string })[] // "month": "2024-07"
  }
  categoryRatings: ChannelReviewStatCategoryRatingItem[]
  averageRating: Record<
    ChannelReviewSources,
    { reviewsCount: number; averageRating: number; channelAverageRating: number }
  >
  rentalAverageOverallRating: ChannelRentalAVGRatingItem[]
  monthlyRatingDistribution: ChannelMonthlyRatingDistItem[]
  yearlyFiveStarRating: { year: number; count: number }[]
  categoryRatingsByRental: ReviewsStatsCategoryRatingByRental[]
  fiveStarPercentageByRental: FiveStarPercentageByRental[]
  fiveStarPercentageByPeriod: FiveStarPercentageByPeriod[]
}

export enum ChannelReviewStatScopes {
  Counts = 'counts',
  AverageRating = 'averageRating',
  MonthlyRatingDistribution = 'monthlyRatingDistribution',
  RentalAverageOverallRating = 'rentalAverageOverallRating',
  YearlyFiveStarRating = 'yearlyFiveStarRating',
  FiveStarPercentageByRental = 'fiveStarPercentageByRental',
  FiveStarPercentageByPeriod = 'fiveStarPercentageByPeriod',
  CategoryRatings = 'categoryRatings',
  CategoryRatingsByRental = 'categoryRatingsByRental',
}

export interface ChannelReviewSearchParams {
  ids?: string[] // get reviews by internal review ids
  channelReviews?: string[] // get reviews by channel review ids
  limit?: number
  page?: number
  sources?: ChannelReviewSources[]
  properties?: string[]
  listings?: string[]
  rentals?: string[]
  guests?: string[]
  inquiries?: string[]
  submitted?: boolean
  submittedAfter?: number
  submittedBefore?: number
  responded?: boolean
  includeUnsubmittedByHost?: boolean
  includeDeletedInquiries?: boolean
  includeStats?: boolean
  reviewerRole?: 'host' | 'guest'
  reviewerCanSubmit?: boolean
  scopes?: ChannelReviewStatScopes[]
  categoryRating?: ReviewCategoryRatingFilter
  overallRating?: number[] // Exact overall ratings to match (unique values from 1 to 5)
  channelOverallRating?: number[] // Exact channel overall ratings to match (unique values from 1 to 5/10 based on the channel)
}

export interface ChannelReviewFetchParams {
  limit?: number
  page?: number
  source?: ChannelReviewSources
  propertyId?: string
  listingId?: string
  rentalId?: string
  guestId?: string
  inquiryId?: string
}

export interface ChannelReviewImportPayload {
  connectedProperties?: boolean
  connectedPropertiesChannels?: string[]
  skipPropertiesWithNoConnectedListings?: boolean
  properties?: {
    id: string
    channel: string // 'airbnb'
  }[]
}

export interface ReviewCategoryRatingForAI {
  type: string
  stars: number
  tags?: string[]
  comment?: string
}

export interface AirbnbAIGuestReviewPayload {
  inquiryId: string
  categories: ReviewCategoryRatingForAI[]
  account: number
}

export interface AirbnbAIGuestReviewResponsePayload {
  inquiryId: string
  account: number
  review: string
  categories: ReviewCategoryRatingForAI[]
}

export function getAIReviewCategoriesAvgRating(ratings: ReviewCategoryRatingForAI[]): number {
  if (!ratings?.length) return 0
  return +(ratings?.reduce((acc, cur) => acc + cur.stars, 0) / ratings.length).toFixed()
}
